<script lang="ts">
	import { currentMarketIdentifier, limitOrders, userBalances } from '$lib/home/store';
	import {
		accessibleMarginAdjustPopup,
		activeSection,
		adjustMarginMarket,
		depositInProgress,
		marginAdjustPopup,
		marginInput,
		marginInputValid,
		withdrawInProgress
	} from '$lib/home/userInteract/store';
	import DisabledButton from '$lib/util/DisabledButton.svelte';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import ProcessingButton from '$lib/util/ProcessingButton.svelte';
	import SwitchToOptimismButton from '$lib/util/SwitchToOptimismButton.svelte';
	import { getNumBackInUnits, getShortAddress, sanitizeInputs, toBN } from '$lib/util/util';
	import { chain, walletConnected, walletLoaded } from '$lib/wallet/store';
	import { MARKET_ADDRESES } from '$lib/web3/addresses';
	import BigNumber from 'bignumber.js';
	import { executeDeposit, executeWithdraw } from './adjustMargin';
	import AdjustMarginMinDepositButton from './AdjustMarginMinDepositButton.svelte';
	import AdjustMarginTitle from './AdjustMarginTitle.svelte';
	import { getDisplayName } from '$lib/perps';

	let localinput: string = '';
	function setMax() {
		if ($activeSection == 'Deposit') {
			$marginInput = $userBalances.sUSD.balanceUSD;
		} else {
			$marginInput = $accessibleMarginAdjustPopup;
		}
		localinput = getNumBackInUnits($marginInput, 18, 2);
	}

	function inputChanged() {
		localinput = sanitizeInputs(localinput);
		if (localinput) $marginInput = toBN(localinput).toString();
		else $marginInput = '0';
	}

	$: if ($activeSection) {
		localinput = '';
		$marginInput = '';
		$depositInProgress = false;
		$withdrawInProgress = false;
	}

	let minDepositInputNeeded: string = '0';
	let isMinMarginAfterDepositSufficient: boolean = false;
	$: if ($marginAdjustPopup && $marginInput) {
		minDepositInputNeeded = new BigNumber('50')
			.multipliedBy(1e18)
			.minus(new BigNumber($marginAdjustPopup))
			.toString();
		isMinMarginAfterDepositSufficient = new BigNumber($marginInput).gte(
			new BigNumber(minDepositInputNeeded)
		);
	}
</script>

<GradientBorder
	gradient="sm:bg-fx-gradient-6"
	paddingClass="p-px"
	borderRadius="rounded-2xl squircle-2xl"
	outerClassList="w-full sm:w-fit h-fit"
	innerClassList="bg-fx-black"
>
	<div class="bg-fx-gradient-8-50 text-fx-white p-4 sm:p-8 rounded-2xl">
		<div class="flex gap-4">
			<AdjustMarginTitle title={'Deposit'} />
			<AdjustMarginTitle title={'Withdraw'} />
		</div>
		<div class="border-t border-t-fx-grey-800 w-full my-6" />
		<div class="text-fx-grey-400 text-xs sm:text-sm mb-4 grid justify-between">
			<div>
				Market:
				{#if $adjustMarginMarket}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm"
						>{getDisplayName($adjustMarginMarket)}</span
					>
				{:else}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm">--</span>
				{/if}
			</div>
			<div>
				Total Margin:
				{#if $marginAdjustPopup}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm"
						>${getNumBackInUnits($marginAdjustPopup, 18, 2)}</span
					>
				{:else}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm">--</span>
				{/if}
			</div>
			<div>
				Margin Available:
				{#if $marginAdjustPopup}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm"
						>${getNumBackInUnits($accessibleMarginAdjustPopup, 18, 2)}</span
					>
				{:else}
					<span class="text-fx-grey-200 font-semibold ml-1 text-sm">--</span>
				{/if}
			</div>
		</div>
		{#if $activeSection == 'Deposit'}
			<div
				class="px-5 py-4 border border-fx-grey-500 rounded-xl squircle-xl bg-fx-gradient-8-50 sm:min-w-[350px]"
			>
				<div class="flex justify-between">
					<div class="text-fx-grey-100 text-sm">Deposit Amount</div>

					<div class="relative flex items-center">
						<div
							class="cursor-pointer flex items-center gap-2 px-3 py-1 border border-fx-grey-700 bg-fx-grey-900 rounded-3xl hover:bg-fx-grey-700"
						>
							<img loading="lazy" src={'tokens/susd.png'} alt="susd" class="w-7 h-7" />

							<div class="text-fx-grey-200 text-sm font-medium">sUSD</div>
						</div>
					</div>
				</div>
				<div class="border-t border-t-fx-grey-800 w-full my-3" />
				<div class="flex justify-between">
					<div class="flex items-end gap-1">
						<input
							inputmode="decimal"
							autocomplete="off"
							autocorrect="off"
							minlength="1"
							maxlength="79"
							spellcheck="false"
							pattern="^[0-9]*[.,]?[0-9]*$"
							bind:value={localinput}
							on:input={inputChanged}
							type="string"
							placeholder="0"
							class="bg-transparent text-fx-white font-medium outline-none text-xl sm:max-w-[120px] max-w-[80px]"
							data-testid="deposit-amount-input"
						/>
					</div>
					<div class="flex items-center gap-2">
						<div class="text-fx-grey-400 text-xs">
							Balance:
							{#if $userBalances}
								<span class="text-fx-grey-200 text-xs" data-testid="available-balance-for-deposit"
									>{getNumBackInUnits($userBalances.sUSD.balanceUSD, 18, 2)} sUSD</span
								>
							{:else}
								<span class="text-fx-grey-200 text-xs">--</span>
							{/if}
						</div>

						<GradientBorder
							gradient="bg-fx-gradient-12"
							paddingClass="p-px"
							borderRadius="rounded-sm squircle-sm"
							outerClassList="hidden sm:block"
							innerClassList="flex justify-between items-center bg-fx-black"
						>
							<button
								class="cursor-pointer px-1 py-1 bg-fx-gradient-8 text-xs text-fx-grey-200"
								on:click={setMax}
								on:keydown={setMax}
							>
								MAX
							</button>
						</GradientBorder>
					</div>
				</div>
			</div>
			{#if $walletLoaded && $walletConnected && $chain?.id != 10}
				<div class="my-4 sm:my-6"><SwitchToOptimismButton /></div>
			{:else if !new BigNumber($userBalances && $userBalances.sUSD.balance).gt(100000000000000000)}
				<a rel="noreferrer" href="https://app.1inch.io/#/10/simple/swap/USDC/SUSD" target="_blank">
					<div
						class="my-4 sm:my-6 rounded-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm"
					>
						Get sUSD <span class="text-fx-grey-600 text-xs">(min. deposit $50)</span>
					</div>
				</a>
			{:else if !$marginInputValid}
				<div class="my-4 sm:my-6">
					<DisabledButton title={'Deposit'} />
				</div>
			{:else if !isMinMarginAfterDepositSufficient}
				<div class="my-4 sm:my-6">
					<AdjustMarginMinDepositButton
						title={'Min. Deposit is $' + getNumBackInUnits(minDepositInputNeeded, 18, 2)}
					/>
				</div>
			{:else if $depositInProgress}
				<div class="my-4 sm:my-6">
					<ProcessingButton title={'Processing Deposit'} />
				</div>
			{:else}
				<button
					class="my-4 sm:my-6 rounded-xl squircle-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm w-full"
					on:click={() => executeDeposit($marginInput, $userBalances.sUSD.balanceUSD)}
					on:keydown={() => executeDeposit($marginInput, $userBalances.sUSD.balanceUSD)}
					data-testid="margin-deposit-btn"
				>
					Deposit
				</button>
			{/if}
		{:else}
			<div
				class="px-5 py-4 border border-fx-grey-500 rounded-xl squircle-xl bg-fx-gradient-8-50 sm:min-w-[350px]"
			>
				<div class="flex justify-between">
					<div class="text-fx-grey-100 text-sm">Withdraw Amount</div>

					<div class="relative flex items-center">
						<div
							class="flex items-center gap-2 px-3 py-1 border border-fx-grey-700 bg-fx-grey-900 rounded-3xl hover:bg-fx-grey-700"
						>
							<img loading="lazy" src={'tokens/susd.png'} alt="susd" class="w-7 h-7" />

							<div class="text-fx-grey-200 text-sm font-medium">sUSD</div>
						</div>
					</div>
				</div>
				<div class="border-t border-t-fx-grey-800 w-full my-3" />
				<div class="flex justify-between">
					<div class="flex items-end gap-1">
						<input
							inputmode="decimal"
							autocomplete="off"
							autocorrect="off"
							minlength="1"
							maxlength="79"
							spellcheck="false"
							pattern="^[0-9]*[.,]?[0-9]*$"
							bind:value={localinput}
							on:input={inputChanged}
							type="text"
							placeholder="0"
							class="bg-transparent text-fx-white font-medium outline-none text-xl sm:max-w-[120px] max-w-[80px]"
							data-testid="withdraw-amount-input"
						/>
					</div>
					<div class="flex items-center gap-2">
						<div class="text-fx-grey-400 text-xs">
							Balance:

							{#if $marginAdjustPopup}
								<span class="text-fx-grey-200 text-xs" data-testid="available-balance-for-withdraw">
									{getNumBackInUnits($accessibleMarginAdjustPopup, 18, 2)} sUSD</span
								>
							{:else}
								<span class="text-fx-grey-200 text-xs">--</span>
							{/if}
						</div>

						<GradientBorder
							gradient="bg-fx-gradient-12"
							paddingClass="p-px"
							borderRadius="rounded-sm squircle-sm"
							outerClassList="hidden sm:block"
							innerClassList="flex justify-between items-center bg-fx-black"
						>
							<button
								class="cursor-pointer px-1 py-1 bg-fx-gradient-8 text-xs text-fx-grey-200"
								on:click={setMax}
								on:keydown={setMax}
							>
								MAX
							</button>
						</GradientBorder>
					</div>
				</div>
			</div>
			{#if $limitOrders.length > 0}
				<div class=" my-2 border squircle-xl rounded-xl border-fx-grey-600 p-2">
					<div class="text-fx-red text-sm font-semibold">You have limit orders in queue.</div>
					<div class="text-fx-600 text-xs">
						Your limit order won't be executed if you withdraw margin.
					</div>
				</div>
			{/if}

			{#if $walletLoaded && $walletConnected && $chain?.id != 10}
				<div class="my-4 sm:my-6"><SwitchToOptimismButton /></div>
			{:else if !$marginInputValid}
				<div class="my-4 sm:my-6">
					<DisabledButton title={'Withdraw'} />
				</div>
			{:else if $withdrawInProgress}
				<div class="my-4 sm:my-6">
					<ProcessingButton title={'Processing Withdraw'} />
				</div>
			{:else}
				<button
					class="my-4 sm:my-6 rounded-xl squircle-xl bg-fx-gradient-9 py-3 sm:py-4 text-fx-black font-semibold text-center cursor-pointer text-xs sm:text-sm w-full"
					on:click={() => executeWithdraw($marginInput, $accessibleMarginAdjustPopup)}
					on:keydown={() => executeWithdraw($marginInput, $accessibleMarginAdjustPopup)}
					data-testid="margin-withdraw-btn"
				>
					Withdraw
				</button>
			{/if}
		{/if}
		<div class="border-t border-t-fx-grey-800 w-full mb-6" />
		<div class="flex justify-between items-center">
			<a rel="noreferrer" href="https://app.1inch.io/#/10/simple/swap/USDC/SUSD" target="_blank"
				><div class="flex gap-1 items-center">
					<div class="text-xs text-fx-pink">Get sUSD</div>
					<img loading="lazy" src={'link-grey.png'} class="w-4 h-4" alt="help" />
				</div>
			</a>
			<div class="text-xs text-fx-grey-400">
				Contract address:<span class="text-fx-yellow font-medium ml-1"
					>{getShortAddress(MARKET_ADDRESES[$currentMarketIdentifier])}</span
				>
			</div>
		</div>
	</div>
</GradientBorder>

<style>
	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
</style>
